import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { useTutorial } from "@/lib/tutorial";

const CARD_W = 330;

// Find the on-screen element for a tour target. Some targets render twice
// (desktop nav + a mobile `<target>-m` variant); pick whichever is actually
// VISIBLE on the current viewport so the spotlight works on mobile too.
function findVisibleTarget(testid) {
  if (!testid) return null;
  for (const id of [testid, `${testid}-m`]) {
    const els = document.querySelectorAll(`[data-testid="${id}"]`);
    for (const el of els) {
      const r = el.getBoundingClientRect();
      // Visible = has real size. (offsetParent is null for position:fixed elements
      // like the floating "+" FAB, so we can't rely on it; display:none nav items
      // collapse to 0×0 and are correctly skipped.)
      if (r.width > 0 && r.height > 0) return el;
    }
  }
  return null;
}

export default function TutorialOverlay() {
  const { running, index, step, total, next, prev, stop, skip } = useTutorial();
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!running || !step) return;
    const update = () => {
      if (!step.target) { setRect(null); return; }
      const el = findVisibleTarget(step.target);
      if (el) {
        el.scrollIntoView({ block: "nearest", inline: "nearest" });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setRect(null);
      }
    };
    update();
    const id = setTimeout(update, 80);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      clearTimeout(id);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [running, index, step]);

  useEffect(() => {
    if (!running) return;
    const onKey = (e) => {
      if (e.key === "Escape") stop();
      else if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [running, next, prev, stop]);

  if (!running || !step) return null;

  const cardW = Math.min(CARD_W, (typeof window !== "undefined" ? window.innerWidth : CARD_W) - 24);
  const pad = 8;
  const spot = rect
    ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }
    : null;

  // Tooltip position for the spotlight case (centered case uses a flex wrapper).
  let tip = null;
  if (spot) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.min(Math.max(12, spot.left), vw - cardW - 12);
    const spaceBelow = vh - (spot.top + spot.height);
    tip = spaceBelow > 230
      ? { top: spot.top + spot.height + 14, left }
      : { top: Math.max(12, spot.top - 14 - 210), left };
  }

  const cardVisual = {
    width: cardW,
    background: "linear-gradient(160deg, #1b1828, #100e18)",
    border: "1px solid rgba(124,92,255,0.5)",
    boxShadow: "0 24px 70px -18px rgba(124,92,255,0.6)",
  };

  const CardContent = (
    <>
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#C9B6FF]">
          <Sparkles size={13} /> Step {index + 1} / {total}
        </div>
        <button onClick={skip} data-testid="tutorial-skip" className="text-[#A8A6B4] hover:text-white transition-colors" aria-label="Skip tour">
          <X size={16} />
        </button>
      </div>
      <div className="text-[17px] font-bold text-white leading-tight">{step.title}</div>
      <p className="text-[13.5px] text-[#E2E0EC] mt-2 leading-relaxed">{step.body}</p>

      <div className="flex items-center gap-1.5 mt-4">
        {Array.from({ length: total }).map((_, i) => (
          <span key={i} className="h-1 rounded-full transition-all"
            style={{ width: i === index ? 18 : 6, background: i <= index ? "#7C5CFF" : "rgba(255,255,255,0.22)" }} />
        ))}
      </div>

      <div className="flex items-center justify-between mt-4">
        <button onClick={skip} data-testid="tutorial-skip-text" className="text-[12px] text-[#A8A6B4] hover:text-white transition-colors">
          Skip tour
        </button>
        <div className="flex items-center gap-2">
          {index > 0 && (
            <button onClick={prev} data-testid="tutorial-prev" className="btn btn-secondary px-3 py-2 text-[12px]">
              <ArrowLeft size={13} /> Back
            </button>
          )}
          <button onClick={next} data-testid="tutorial-next" className="btn btn-primary px-4 py-2 text-[12px]">
            {index >= total - 1 ? "Finish" : <>Next <ArrowRight size={13} /></>}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(
    <div className="fixed inset-0 z-[200]" data-testid="tutorial-overlay">
      {spot ? (
        <motion.div
          layout
          className="absolute pointer-events-none"
          data-testid="tutorial-spotlight"
          style={{
            top: spot.top, left: spot.left, width: spot.width, height: spot.height,
            boxShadow: "0 0 0 9999px rgba(7,7,12,0.76)",
            border: "2px solid rgba(124,92,255,0.95)",
            borderRadius: 16,
          }}
          transition={{ type: "spring", stiffness: 320, damping: 32 }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: "rgba(7,7,12,0.78)" }} />
      )}

      {/* click anywhere to advance */}
      <div className="absolute inset-0" onClick={next} />

      {spot ? (
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="absolute rounded-2xl p-5"
            style={{ ...tip, ...cardVisual }}
            data-testid="tutorial-card"
          >
            {CardContent}
          </motion.div>
        </AnimatePresence>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="rounded-2xl p-5 pointer-events-auto"
              style={cardVisual}
              data-testid="tutorial-card"
            >
              {CardContent}
            </motion.div>
          </AnimatePresence>
        </div>
      )}
    </div>,
    document.body
  );
}
